import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MenuIcon from "@mui/icons-material/Menu";
import SearchIcon from "@mui/icons-material/Search";
import { Container, InputBase } from "@mui/material";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { alpha, styled, useTheme } from "@mui/material/styles";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import * as React from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";

import logo from "../assets/Images/LogoB.png";
import BasicPopover from "../components/common-components/BasicPopover";
import {
  DashboardIconBlue,
  DashboardIconWhite,
  DividerIcon,
  DrawerIcon,
  InvoiceIcon,
  InvoiceIconBlue,
  OrderIcon,
  OrderIconBlue,
  ServiceIconBlue,
  ServiceIconWhite,
  UserIconBlue,
  UserIconWhite,
} from "../assets/Icon/headerCicon";


const drawerWidth = 258;

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })<{
  open?: boolean;
  isMobile?: boolean;
}>(({ theme, open, isMobile }) => ({
  flexGrow: 1,

  padding: theme.spacing(3),
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.standard * 2,
  }),

  marginLeft: isMobile
    ? open
      ? `${drawerWidth}px`
      : "0px"
    : `-${drawerWidth}px`,
  variants: [
    {
      props: ({ open }) => open,
      style: {
        transition: theme.transitions.create("margin", {
          easing: theme.transitions.easing.easeInOut,
          duration: theme.transitions.duration.standard * 2,
        }),
        marginLeft: 0,
      },
    },
  ],
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
  isMobile?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, isMobile }) => ({
  // transition: theme.transitions.create(["margin", "width"], {
  //   easing: theme.transitions.easing.easeInOut,
  //   duration: theme.transitions.duration.standard * 2,
  // }),
  variants: [
    {
      props: ({ open }) => open,
      style: {
        width: !isMobile && `calc(100% - ${drawerWidth}px)`,
        marginLeft: `-${drawerWidth}px`,
        // transition: theme.transitions.create(["margin", "width"], {
        //   easing: theme.transitions.easing.easeInOut,
        //   duration: theme.transitions.duration.standard * 2,
        // }),
      },
    },
  ],
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
}));
const Search = styled("div")(({ theme }) => ({
  position: "relative",
  height: "46px",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: "100%",
  [theme.breakpoints.up("sm")]: {
    marginLeft: theme.spacing(3),
    width: "auto",
  },
}));

export const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));
const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create("width"),
    width: "100%",
    [theme.breakpoints.up("md")]: {
      width: "20ch",
    },
  },
}));

export default function AdminLayout() {
  const [selectedItem, setSelectedItem] = React.useState<number>(0);
  const { page } = useParams();
  const [title, setTitle] = React.useState<string>("Dashboard");
  const navigate = useNavigate();
  const handleItemClick = (index: number, item: string) => {
    setTitle(item);
    setSelectedItem(index);
    if (isMobile) {
      setOpen(false);
    }
  };

  React.useEffect(() => {
    let newSelectedItem = 0;
    let newTitle = "Dashboard";

    if (window.location.pathname.includes("admin-orders")) {
      newSelectedItem = 1;
      newTitle = "Orders";
    } else if (window.location.pathname.includes("admin-clients")) {
      newSelectedItem = 2;
      newTitle = "Clients";
    } else if (window.location.pathname.includes("admin-services")) {
      newSelectedItem = 3;
      newTitle = "Services";
    } else if (window.location.pathname.includes("admin-invoices")) {
      newSelectedItem = 4;
      newTitle = "Invoices";
    }

    setSelectedItem(newSelectedItem);
    setTitle(newTitle);
  }, [page, window.location.pathname]);

  /************************************************** Admin NavBar Items ***********************************************************/
  const adminMenuItems = [
    {
      text: "Dashboard",
      activeIcon: <DashboardIconBlue />,
      icon: <DashboardIconWhite />,
      hasSubmenu: false,
      onClick: () => {
        navigate("/admin-dashboard");
      },
    },
    {
      text: "Orders",
      activeIcon: <OrderIcon />,
      icon: <OrderIconBlue />,
      hasSubmenu: false,
      onClick: () => {
        navigate("/admin-orders");
      },
    },
    {
      text: "Clients",
      activeIcon: <UserIconBlue />,
      icon: <UserIconWhite />,
      hasSubmenu: false,
      onClick: () => {
        navigate("/admin-clients");
      },
    },
    // {
    //   text: "Services",
    //   activeIcon: <ServiceIconBlue />,
    //   icon: <ServiceIconWhite />,
    //   hasSubmenu: false,
    //   onClick: () => {
    //     navigate("/admin-services");
    //   },
    // },
    {
      text: "Invoices",
      icon: <InvoiceIcon />,
      activeIcon: <InvoiceIconBlue />,
      hasSubmenu: false,
      onClick: () => navigate("/admin-invoices"),
    },
  ];
  /************************************************** User NavBar Items ***********************************************************/

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [open, setOpen] = React.useState(!isMobile);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        open={open}
        isMobile={isMobile}
        sx={{ background: "white" }}
      >
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            zIndex: (theme) => theme.zIndex.drawer - 9,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {!open ? (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                onClick={handleDrawerOpen}
                edge="start"
                sx={[
                  {
                    mr: 2,
                    color: theme.palette.myCustomColor.dark,
                  },
                  open && { display: "none" },
                ]}
              >
                <MenuIcon />
              </IconButton>
            ) : (
              <IconButton
                onClick={handleDrawerClose}
                sx={{
                  mr: 2,
                  position: "absolute",
                  left: -20,
                  zIndex: 3,
                }}
              >
                {theme.direction === "ltr" ? (
                  <DrawerIcon />
                ) : (
                  <ChevronRightIcon />
                )}
              </IconButton>
            )}
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                color: theme.palette.myCustomColor.dark,
                fontSize: 20,
                fontWeight: 500,
              }}
            >
              {title !== "Home" ? "" : "Dashborad"}
              {/* {title !== "Home" ? title : "Dashborad"} */}
            </Typography>
          </Box>
          <Box sx={{ display: { xs: "flex" } }}>
            {/* <Search
              sx={{
                background: "#F6F9FD",
                color: "#6B6B6B",
                borderRadius: "8px",
                "&:hover": {
                  background: "#F6F9FD", // Correct hover styling
                },
              }}
            >
              <SearchIconWrapper>
                <SearchIcon />
              </SearchIconWrapper>
              <StyledInputBase
                placeholder="Search here…"
                inputProps={{ "aria-label": "search" }}
              />
            </Search> */}

            {/* <IconButton size="large" aria-label="show 17 new notifications">
              <Badge badgeContent={17} color="error">
                <NotificationIcon />
              </Badge>
            </IconButton> */}

            <BasicPopover />
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            background: theme.palette.myCustomColor.link,
            zIndex: 2,
            // transition: "none",
            // transition: "width 0.6s ease !important",
          },
        }}
        variant={isMobile ? "temporary" : "persistent"}
        anchor="left"
        open={open}
      >
        <DrawerHeader>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              paddingTop: "20px",
              gap: 1,
              width: "100%",
              marginBottom: "20px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <div
                style={{
                  paddingLeft: "20px",
                }}
              >
                <img
                  src={logo}
                  alt=""
                  style={{
                    width: "150px",
                    height: "34px",
                    objectFit: "cover",
                  }}
                />
              </div>

              <IconButton
                color="inherit"
                aria-label="open drawer"
                onClick={() => setOpen(!open)}
                edge="start"
                sx={[
                  {
                    mr: 2,
                    color: "white",
                  },
                  !isMobile && { display: "none" },
                ]}
              >
                <MenuIcon />
              </IconButton>
            </Box>
            <DividerIcon />
          </Box>
        </DrawerHeader>

        <List>
          {adminMenuItems.map((item, index) => {
            return (
              <ListItem
                key={index}
                disablePadding
                sx={{
                  background: selectedItem == index ? "white" : "tranparent",
                  color: selectedItem == index ? "#2977CC" : "white",
                }}
                onClick={item.onClick}
              >
                <ListItemButton
                  onClick={() => handleItemClick(index, item?.text)}
                >
                  {item?.icon ? (
                    <>
                      <ListItemIcon>
                        {selectedItem == index ? item.activeIcon : item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.text}
                        sx={{
                          "& .MuiTypography-root": {
                            margin: 0,
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                            fontWeight: 500,
                            fontSize: "14px",
                            lineHeight: 1.5,
                          },
                        }}
                      >
                        {item.text}
                      </ListItemText>
                    </>
                  ) : (
                    <>
                      <ListItemText
                        primary={item.text}
                        sx={{
                          "& .MuiTypography-root": {
                            margin: 0,
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                            fontWeight: 500,
                            fontSize: "14px",
                            lineHeight: 1.5,
                          },
                        }}
                      >
                        {item.text}
                      </ListItemText>
                    </>
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Drawer>

      <Main
        open={open}
        isMobile={isMobile}
        sx={{
          background: "#F6F9FD",
          width: isMobile ? "100vw" : "calc(100vw - 258px)", // Fixes layout on mobile
          height: "calc(100vh - 10px)",
          overflowX: "hidden",
        }}
      >
        <DrawerHeader />
        {/* <Container> */}
        <Outlet />
      </Main>
      {/* </Container> */}
    </Box>
  );
}
